import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '@/store/users/actions';
import { selectUsers } from '@/store/users/selectors';

/**
 * Warms the account user directory into the Redux store, off the critical path.
 *
 * The notification bell is mounted in the app shell, so its `fetchUsers` on
 * mount ran on **every** full page load — including settings, tasks and reports,
 * none of which read the result. Sentry measured `GET /api/v1/users` at p75
 * 1,644ms on /tasks/jobs/scorecard, p75 1,607ms on /settings/api-management and
 * p75 5,835ms / p95 26,008ms on /settings/offices, competing with each page's
 * own requests while it loaded.
 *
 * It is deferred rather than removed because the prefetch is load-bearing
 * elsewhere: surfaces such as `candidate-job-drawer/ContactInfo` read
 * `selectUsers` without ever dispatching the fetch themselves, so dropping it
 * would leave them with an empty directory.
 *
 * The timeout is a ceiling, not a delay — a permanently busy tab still fetches
 * rather than deferring forever. Older Safari falls back to a macrotask, which
 * still yields to paint.
 */
export const useDeferredUserDirectory = () => {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  // Read through a ref-like check rather than a dep: this must fire once per
  // mount, and depending on `users` would re-run it as the fetch resolves.
  const alreadyLoaded = Boolean(users?.length);

  useEffect(() => {
    if (alreadyLoaded) return undefined;

    const run = () => dispatch(fetchUsers(undefined));

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(run, 1);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount; `alreadyLoaded` is read as the initial condition only
  }, []);
};
